import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { getOptimizationTargetKey } from "./predictUtils";
import { formatSuggestedSettings } from "../../utils/Utils";
import { truncateFloat } from "../../utils/appUtils";
import { APP_SCOPE } from "../../constants/global.constants";
import { getRouteAppScope } from "../../network/networkUtil";
import { getChemicalOptionsByCategory } from "../../components/ChemicalSelectionFields/utils";

const ExportAllData = () => {
  const { t, i18n } = useTranslation();
  const cips = useSelector((state) => state.cips);
  const cleanData = useSelector((state) => state.cleanData);
  const cleaningEffectiness = useSelector((state) => state.cleaningEffectiness);
  const fouling = useSelector((state) => state.fouling);
  const flow = useSelector((state) => state.flow);
  const pressure = useSelector((state) => state.pressure);
  const water_type = useSelector((state) => state.water_type);
  const feed = useSelector((state) => state.feed_quality);
  const permeate = useSelector((state) => state.permeate_quality);
  const temperature = useSelector((state) => state.temperature);
  const flux = useSelector((state) => state.flux);
  const volume = useSelector((state) => state.volume);
  const currency = useSelector((state) => state.currency);
  const mass = useSelector((state) => state.mass);
  const conversion_rate = useSelector((state) => state.conversion_rate);
  const feed_quality = useSelector((state) => state.feed_quality);
  const permeate_quality = useSelector((state) => state.permeate_quality);
  const engryPrice = useSelector(
    (state) => state.trainUtilityCosts.energy_price,
  );
  const cipWaterPrice = useSelector(
    (state) => state.trainUtilityCosts?.water_price,
  );
  const costofDownTime = useSelector(
    (state) => state.trainUtilityCosts?.downtime_cost,
  );
  const alkali_used_per_cip = useSelector((state) => state.alkali_used_per_cip);
  const acid_used_per_cip = useSelector((state) => state.acid_used_per_cip);
  const defaultCIPAcidPrice = useSelector((state) => state.acid_price);
  const defaultCIPAlkaliPrice = useSelector((state) => state.alkali_price);
  const defaultCIPAdditivePrice = useSelector(
    (state) => state.additional_chemical_price,
  );
  const acid_chem_id = useSelector((state) => state.acid_chem_id);
  const alkali_chem_id = useSelector((state) => state.alkali_chem_id);
  const additional_chemical_chem_id = useSelector(
    (state) => state.additional_chemical_chem_id,
  );
  const plantChemicalLibrary = useSelector((state) => state.plantChemicalLibrary);
  const additional_chemical_per_cip = useSelector(
    (state) => state.additional_chemical_per_cip,
  );
  const acid_cip_ph = useSelector((state) => state.acid_cip_ph);
  const alkali_cip_ph = useSelector((state) => state.alkali_cip_ph);
  const cip_solution_ph = useSelector((state) => state.cip_solution_ph);
  const acid_cip_temp = useSelector((state) => state.acid_cip_temp);
  const alkali_cip_temp = useSelector((state) => state.alkali_cip_temp);
  const cip_solution_temp = useSelector((state) => state.cip_solution_temp);
  const acid_cip_duration = useSelector((state) => state.acid_cip_duration);
  const alkali_cip_duration = useSelector((state) => state.alkali_cip_duration);
  const cip_solution_duration = useSelector(
    (state) => state.cip_solution_duration,
  );
  const plantName = useSelector((state) => state.plantName);
  const plantLocation = useSelector((state) => state.plantLocation);
  const num_trains = useSelector((state) => state.num_trains);
  const dataset_name = useSelector((state) => state.dataset_name);
  const tol_num_elements = useSelector((state) => state.tol_num_elements);
  const is_dupont_element = useSelector((state) => state.is_dupont_element);
  const sel_pass = useSelector((state) => state.sel_pass);
  const sel_train = useSelector((state) => state.sel_train);
  const sel_stage = useSelector((state) => state.sel_stage);
  const sel_pressure_vessels = useSelector(
    (state) => state.sel_pressure_vessels,
  );
  const sel_element_involved = useSelector(
    (state) => state.sel_element_involved,
  );
  const sel_element_type = useSelector((state) => state.sel_element_type);
  const feed_water_factor = useSelector((state) => state.feed_water_factor);
  const feed_water_permeate_factor = useSelector(
    (state) => state.feed_water_permeate_factor,
  );
  const {
    n_neighbors: clusterPoints,
    window: medianCurvePoints,
    n: maxRelativeDistance,
    pf_threshold: permeateFlowDropThreshold,
    pressure_drop_threshold: pressureDropThreshold,
    pressure_drop_abs_threshold: pressureDropThresholdAbsolute,
    salt_passage_threshold: saltPassageIncreaseThreshold,
    min_day_between_cips: minDaysBetweenCIPs,
    max_days_between_cips: maxDaysBetweenCIPs,
    k: cipIdentificationResolution,
    shutdown_length: minIdleDaysShutdown,
    use_permeability_trigger: usePermeabilityTrigger,
    permeability_only: permeabilityOnly,
    permeability_threshold: permeabilityThreshold,
    use_salt_passage: useSaltPassageChange,
    min_time: minStabilizationDaysAfterCIP,
    max_time: maxStabilizationDaysAfterCIP,
    min_num_points: minPointsForCleaningEffectiveness,
    stabilization_days: deafultStabilizationDaysAfterCIP,
    top_exp: foulingAggressivenessFactor,
    max_evals: numberOfIterationsToFindOptima,
    adapt_pf_trend: adaptPermeateFlowDropThreshold,
    adapt_pressure_drop_trend: adaptPressureDropThreshold,
    adapt_salt_passage_trend: adaptSaltPassageThreshold,
    simulation_improve_accuracy: simulationImproveAccuracy,
    failure_probability_threshold: failureProbabilityThreshold,
    optimization_target: optimizationTarget,
  } = useSelector((state) => state.advancedSettings);

  const installation_date = useSelector((state) => state.installation_date);
  const warranty = useSelector((state) => state.warranty);
  const suggestedSettings = useSelector((state) => state.suggestedSettings);
  const isRAFlow = getRouteAppScope() === APP_SCOPE.RA;

  const formatAdvancedSettingForExcel = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? truncateFloat(numericValue, 2).toFixed(2)
      : value;
  };

  const chemOptions = useMemo(
    () => getChemicalOptionsByCategory(plantChemicalLibrary || []),
    [plantChemicalLibrary],
  );

  const getLabelById = (options = [], id) =>
    options.find((option) => Number(option.value) === Number(id))?.label || "";

  const defaultCIPSelectedAcid = getLabelById(chemOptions.acid, acid_chem_id);
  const defaultCIPSelectedAlkali = getLabelById(
    chemOptions.alkali,
    alkali_chem_id,
  );
  const defaultCIPSelectedAdditive = getLabelById(
    chemOptions.additional_chemical,
    additional_chemical_chem_id,
  );

  // Format suggested settings for Excel export
  const suggestedOthers = formatSuggestedSettings(suggestedSettings, t);

  const exportAllData = {
    userCips: cips?.filter((item) => !item.is_tool_identified),
    cips,
    cleaningEffectiness,
    fouling,
    cleanData,
    units: {
      flow: flow,
      pressure: pressure,
      feed_quality: feed_quality,
      permeate_quality: permeate_quality,
      temperature: temperature,
    },
    others: [
      {
        plantName,
        plantLocation,
        water_type,
        num_trains,
        tol_num_elements,
        is_dupont_element: is_dupont_element
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        dataset_name,
        ...(isRAFlow && {
          installation_date: installation_date,
          warranty: warranty,
        }),
        sel_train,
        sel_pass,
        sel_stage,
        sel_pressure_vessels,
        sel_element_involved,
        element_type: sel_element_type,
        flow,
        pressure,
        feed,
        feed_water_factor,
        permeate,
        feed_water_permeate_factor,
        temperature: `\u00B0${temperature}`,
        flux,
        volume,
        currency,
        mass,
        exchangeRate: conversion_rate,
        cipWaterPrice,
        costofDownTime,
        engryPrice,
        defaultCIPSelectedAcid,
        defaultCIPAcidPrice,
        defaultCIPSelectedAlkali,
        defaultCIPAlkaliPrice,
        defaultCIPSelectedAdditive,
        defaultCIPAdditivePrice,
        acid_used_per_cip,
        acid_cip_ph,
        acid_cip_temp,
        acid_cip_duration,
        alkali_used_per_cip,
        alkali_cip_ph,
        alkali_cip_temp,
        alkali_cip_duration,
        additional_chemical_per_cip,
        cip_solution_ph,
        cip_solution_temp,
        cip_solution_duration,
        clusterPoints: formatAdvancedSettingForExcel(clusterPoints),
        medianCurvePoints: formatAdvancedSettingForExcel(medianCurvePoints),
        maxRelativeDistance: formatAdvancedSettingForExcel(maxRelativeDistance),
        permeateFlowDropThreshold: formatAdvancedSettingForExcel(
          permeateFlowDropThreshold,
        ),
        pressureDropThreshold: formatAdvancedSettingForExcel(
          pressureDropThreshold,
        ),
        pressureDropThresholdAbsolute: formatAdvancedSettingForExcel(
          pressureDropThresholdAbsolute,
        ),
        useSaltPassageChange: useSaltPassageChange
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        saltPassageIncreaseThreshold: formatAdvancedSettingForExcel(
          saltPassageIncreaseThreshold,
        ),
        minDaysBetweenCIPs: formatAdvancedSettingForExcel(minDaysBetweenCIPs),
        maxDaysBetweenCIPs: formatAdvancedSettingForExcel(maxDaysBetweenCIPs),
        cipIdentificationResolution: formatAdvancedSettingForExcel(
          cipIdentificationResolution,
        ),
        minIdleDaysShutdown: formatAdvancedSettingForExcel(minIdleDaysShutdown),
        usePermeabilityTrigger: usePermeabilityTrigger
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        permeabilityOnly: permeabilityOnly
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        permeabilityThreshold: formatAdvancedSettingForExcel(
          permeabilityThreshold,
        ),
        maxStabilizationDaysAfterCIP: formatAdvancedSettingForExcel(
          maxStabilizationDaysAfterCIP,
        ),
        minStabilizationDaysAfterCIP: formatAdvancedSettingForExcel(
          minStabilizationDaysAfterCIP,
        ),
        minPointsForCleaningEffectiveness: formatAdvancedSettingForExcel(
          minPointsForCleaningEffectiveness,
        ),
        deafultStabilizationDaysAfterCIP: formatAdvancedSettingForExcel(
          deafultStabilizationDaysAfterCIP,
        ),
        foulingAggressivenessFactor: formatAdvancedSettingForExcel(
          foulingAggressivenessFactor,
        ),
        numberOfIterationsToFindOptima: formatAdvancedSettingForExcel(
          numberOfIterationsToFindOptima,
        ),
        adaptPermeateFlowDropThreshold: adaptPermeateFlowDropThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        adaptPressureDropThreshold: adaptPressureDropThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        adaptSaltPassageThreshold: adaptSaltPassageThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        simulationImproveAccuracy: simulationImproveAccuracy
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        failureProbabilityThreshold: formatAdvancedSettingForExcel(
          failureProbabilityThreshold,
        ),
        optimizationTarget: t(
          `advanceSettings.${getOptimizationTargetKey(optimizationTarget)}`,
        ),
      }, //keep the order in sync with OthersTableStrings
    ],
    suggestedOthers,
  };

  return exportAllData;
};

export default ExportAllData;
